import { EmptyChartState } from "@/components/common/EmptyChartState";
import { useAppTheme } from "@/hooks/useAppTheme";
import { Box, Typography } from "@mui/material";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { getChartTooltipStyle } from "../utils/chartStyles";

interface RateCapChartProps {
  rateCapCount: number;
  totalActivePolicies: number;
}

export const RateCapChart = ({ rateCapCount, totalActivePolicies }: RateCapChartProps) => {
  const { theme } = useAppTheme();
  const COLORS = [theme.palette.primary.main, theme.palette.background.default]; // Rate Cap, No Rate Cap

  if (totalActivePolicies === 0) {
    return <EmptyChartState />;
  }

  const rateCap = rateCapCount;
  const noRateCap = totalActivePolicies - rateCapCount;

  const data = [
    { name: "Rate Cap", value: rateCap },
    { name: "No Rate Cap", value: noRateCap },
  ];

  return (
    <Box>
      {/* Custom Legend */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: theme.palette.primary.main,
              borderRadius: 0.5,
            }}
          />
          <Typography variant="caption">Rate Cap</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: theme.palette.background.default,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 0.5,
            }}
          />
          <Typography variant="caption">No Rate Cap</Typography>
        </Box>
      </Box>

      <ResponsiveContainer width="100%" height={250}>
        <PieChart margin={{ top: 0, right: 30, bottom: 10, left: 20 }}>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            stroke={theme.palette.divider}
            strokeWidth={1}
          >
            {data.map((_, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            content={({ payload }) => {
              if (!payload || !payload.length) return null;
              const item = payload[0];
              const value = typeof item.value === "number" ? item.value : 0;
              const total = rateCap + noRateCap;
              const percent = total > 0 ? (value / total) * 100 : 0;
              return (
                <div
                  style={{
                    ...getChartTooltipStyle(theme),
                    padding: "8px",
                  }}
                >
                  <div>
                    <strong>{item.name}</strong>
                  </div>
                  <div>Count: {value}</div>
                  <div>Percentage: {percent.toFixed(1)}%</div>
                </div>
              );
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
};
